<template>
	<Modals
		:title="'Details'"
		:display="true"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="detail-update">{{ actionDetails }}</div>
			<Table
				class="table-container--user-detail-history"
				:sourceObject="userLogData"
				:tableMobilev1="true"
				@change="changedPagination"
			>
				<template #col-group>
					<colgroup>
						<col :class="['table__col table__col--setting']" />
						<col :class="['table__col table__col--change']" />
					</colgroup>
				</template>
				<template #body-changedPropertyName="{body}">
					<div class="user-detail">
						<span
							class="user-detail__changed-property-name"
							v-html="body.changedPropertyName"
						/>
					</div>
				</template>
				<template #body-value="{body}">
					<div class="user-detail">
						<span class="user-detail__value" v-html="body.value" />
					</div>
				</template>
			</Table>
		</template>
		<template #modal-footer>
			<Button
				id="linkButton"
				className="btn--primary"
				labelText="Close"
				:btnClick="close"
			/>
		</template>
	</Modals>
</template>

<script>
import Modals from '@/components/shells/modal/modals';
import Button from '@/components/forms/button/button';
import Table from '@/components/shells/table/table';
import DateTimeMixin from '@/mixins/datetime-mixin';
import { updatePagination } from '@/utils/object';
import _ from 'lodash';

export default {
	name: 'LogRolHistoryDetailModal',
	components: {
		Modals,
		Button,
		Table
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		display: {
			type: Boolean,
			default: false
		},
		logs: {
			type: Object,
			default: () => {}
		}
	},
	mixins: [DateTimeMixin],
	data() {
		return {
			userLogData: {
				headers: [
					{
						key: 'changedPropertyName',
						label: 'Setting',
						sorteable: false
					},
					{
						key: 'value',
						label: 'Change',
						sorteable: false
					}
				],
				source: {},
				displayHeader: true
			},
			page: 1,
			pageSize: 10,
			loading: false,
			logData: {}
		};
	},
	created() {
		this.fetchDetailUser();
	},
	computed: {
		actionDetails() {
			if (_.isEmpty(this.logData)) {
				return '';
			}
			const { timestamp, changedByUserName, action } = this.logData;
			const capitalizeWords = str =>
				str
					.split(' ')
					.map(
						word =>
							word.charAt(0).toUpperCase() +
							word.slice(1).toLowerCase()
					)
					.join(' ');

			const name = capitalizeWords(changedByUserName);

			return `${action}d by ${name} on ${timestamp}`;
		}
	},
	methods: {
		fetchDetailUser() {
			let newSource = updatePagination(
				this.page,
				this.pageSize,
				_.cloneDeep(this.parseUserLogs())
			);
			this.userLogData.source = newSource;
		},
		changedPagination(value) {
			this.page = value.page;
			this.pageSize = value.countPerPage;
		},
		close() {
			this.$emit('close', false);
		},

		parseUserLogs() {
			const { fieldChanges } = this.logData;

			if (_.isEmpty(fieldChanges)) {
				return [];
			}

			const items = fieldChanges.map(change => {
				const {
					ChangedPropertyName,
					Action,
					NewValue,
					OriginalValue
				} = change;

				let normalizedChangedPropertyName = ChangedPropertyName;

				if (normalizedChangedPropertyName.includes('Roles')) {
					normalizedChangedPropertyName = 'Role';
				}

				const changeByActionAndPropertyName = {
					Update: {
						FirstName: `${OriginalValue} to ${NewValue}`,
						LastName: `${OriginalValue} to ${NewValue}`,
						Active: `Set to ${NewValue ? 'Active' : 'Inactive'}`,
						Title: `${OriginalValue} to ${NewValue}`,
						Role: `${OriginalValue?.Role} to ${NewValue?.Role}`
					},
					Delete: {
						Title: `${OriginalValue} Removed`,
						Role: `Removed`
					},
					Add: {
						Title: `${NewValue} Added`,
						FirstName: `${NewValue}`,
						LastName: `${NewValue}`,
						Role: `Added`
					}
				};

				const val =
					changeByActionAndPropertyName[Action][
						normalizedChangedPropertyName
					];

				const changedPropertyName = {
					FirstName: 'First Name',
					LastName: 'Last Name',
					Active: 'User Status',
					Title: 'Title',
					Role: `${
						Action === 'Delete'
							? OriginalValue?.Role
							: NewValue?.Role
					} (${
						NewValue?.ClientId === 'vue-support-portal'
							? 'Internal'
							: 'External'
					} Role)`
				};

				return {
					changedPropertyName:
						changedPropertyName[normalizedChangedPropertyName],
					value: val
				};
			});

			return _.orderBy(
				items,
				[item => item.changedPropertyName.toLowerCase()],
				['asc']
			);
		},
		handleStatusChanges(items, statusRole) {
			const formatStatus = statusRole.newStatusValue
				? 'Active'
				: 'Inactive';
			const newItem = {
				changedPropertyName: 'Role Status',
				newValue: `Set to ${formatStatus}`
			};
			items.push(newItem);

			return items;
		},
		transformLogData(log) {
			const { action, changedByUserName, timestamp } = log;
			if (action !== 'Add') {
				return log;
			}
			const user = log.fieldChanges[0].NewValue;
			const fields = [];

			['FirstName', 'LastName', 'Title'].forEach(field => {
				if (user[field]) {
					fields.push({
						Action: action,
						NewValue: user[field],
						ChangedPropertyName: field
					});
				}
			});

			user.Roles?.forEach(role => {
				fields.push({
					Action: action,
					NewValue: {
						ClientId: role.ClientId,
						Role: role.Role,
						RoleId: role.RoleId
					},
					ChangedPropertyName: `Roles[Role:${role.Role},RoleId:${role.RoleId},ClientId:${role.ClientId}]`
				});
			});

			return {
				action: action,
				changedByUserName,
				fieldChanges: fields,
				timestamp
			};
		}
	},
	watch: {
		display: {
			immediate: true,
			handler() {
				if (!_.isEmpty(this.logs)) {
					this.logData = this.transformLogData(this.logs);
				}
			}
		},
		page: {
			immediate: true,
			handler() {
				this.fetchDetailUser();
			}
		},
		pageSize: {
			immediate: true,
			handler() {
				this.fetchDetailUser();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.detail-update {
	font-family: $font-family;
	font-size: $bd-txt-font-size-sm;
	color: $neutral-2;
	margin-bottom: $gutter-xl;
}
.table-container--user-detail-history {
	.table__col {
		&--setting {
			width: 266px;
		}
		&--change {
			width: 204px;
			text-transform: lowercase;
		}
	}
}

.user-detail {
	&__value {
		text-transform: none;
	}
}
</style>
